// Парсер ошибок Telegram-канала.
// Backend отдаёт ДВА формата (handoff §6):
//   1) Структурный (Telegram-флоу): { detail: { code, detail, retry_after_seconds? } }
//   2) Строковый (гарды 404/409/413/422/503): { detail: "строка" }
// Для строковых 409 — изолированный маппер русских анкоров (как в wabaErrorParser):
// backend переформулирует detail → меняем одну строку здесь.

export type TelegramErrorCode =
  // Структурные коды backend (handoff §15 TelegramErrorCode)
  | 'phone_invalid'
  | 'phone_banned'
  | 'code_invalid'
  | 'code_expired'
  | 'password_invalid'
  | 'flood_wait'
  | 'user_blocked'
  | 'session_revoked'
  | 'telegram_rpc_error'
  | 'no_session_state'
  // Синтезированные из строковых detail / HTTP-статусов
  | 'session_not_connected' // 409 «Сессия не подключена» / «клиент не активен»
  | 'session_state_conflict' // 409 «Сессия в статусе …»
  | 'credentials_locked' // 409 credentials_locked — смена credentials при connected-сессии
  | 'chat_already_closed' // 409 «Чат уже закрыт»
  | 'chat_not_closed' // 409 «Чат не закрыт»
  | 'not_found' // 404 (включая чужую организацию)
  | 'file_too_large' // 413
  | 'not_configured' // 503 — TELEGRAM_API_ID/HASH не настроены
  | 'validation'
  | 'unauthorized'
  | 'forbidden'
  | 'network'
  | 'unknown';

const BACKEND_CODES: ReadonlySet<string> = new Set([
  'phone_invalid',
  'phone_banned',
  'code_invalid',
  'code_expired',
  'password_invalid',
  'flood_wait',
  'user_blocked',
  'session_revoked',
  'telegram_rpc_error',
  'no_session_state',
  'credentials_locked',
]);

export interface TelegramParsedError {
  code: TelegramErrorCode;
  /** Готовое к показу пользователю сообщение (для структурных — текст backend) */
  message: string;
  rawDetail: string;
  status?: number;
  /** Только flood_wait */
  retryAfterSeconds?: number;
  validationFields?: Array<{ loc: (string | number)[]; msg: string; type: string }>;
}

// ─────────────── Anchor map (строковые 409) ───────────────

interface AnchorRule {
  status: number;
  matches: string[];
  code: TelegramErrorCode;
  message: string;
}

const ANCHOR_RULES: AnchorRule[] = [
  {
    status: 409,
    matches: ['сессия не подключена', 'клиент не активен'],
    code: 'session_not_connected',
    message: 'Канал переподключается, попробуйте через минуту.',
  },
  {
    status: 409,
    matches: ['сессия в статусе'],
    code: 'session_state_conflict',
    message: 'Состояние подключения изменилось — синхронизируем шаг.',
  },
  {
    status: 409,
    matches: ['чат уже закрыт'],
    code: 'chat_already_closed',
    message: 'Чат уже закрыт.',
  },
  {
    status: 409,
    matches: ['чат не закрыт'],
    code: 'chat_not_closed',
    message: 'Чат не закрыт.',
  },
  {
    status: 409,
    matches: ['отключите номер', 'credentials_locked'],
    code: 'credentials_locked',
    message: 'Сначала отключите номер, затем меняйте credentials.',
  },
];

// ─────────────── Classifier ───────────────

export function classifyTelegramError(body: unknown, status?: number): TelegramParsedError {
  const detail = (body as { detail?: unknown } | null | undefined)?.detail;

  // 1) Структурный формат Telegram-флоу
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const d = detail as { code?: unknown; detail?: unknown; retry_after_seconds?: unknown };
    const rawCode = typeof d.code === 'string' ? d.code : '';
    const message = typeof d.detail === 'string' && d.detail ? d.detail : 'Неизвестная ошибка';
    return {
      code: BACKEND_CODES.has(rawCode) ? (rawCode as TelegramErrorCode) : 'unknown',
      message,
      rawDetail: message,
      status,
      retryAfterSeconds:
        typeof d.retry_after_seconds === 'number' ? d.retry_after_seconds : undefined,
    };
  }

  // 2) Pydantic-валидация: { detail: [{loc, msg, type}] }
  if (Array.isArray(detail)) {
    const fields = detail as NonNullable<TelegramParsedError['validationFields']>;
    return {
      code: 'validation',
      message: fields[0]?.msg || 'Неверные данные запроса',
      rawDetail: fields.map((f) => f.msg).join('; '),
      status,
      validationFields: fields,
    };
  }

  // 3) Строковый detail + HTTP-статус
  const raw = typeof detail === 'string' ? detail : '';
  const lower = raw.toLowerCase();

  if (status === 401) {
    return { code: 'unauthorized', message: 'Сессия истекла. Войдите заново.', rawDetail: raw, status };
  }
  if (status === 403) {
    return { code: 'forbidden', message: 'Недостаточно прав. Свяжитесь с администратором.', rawDetail: raw, status };
  }
  if (status === 404) {
    return { code: 'not_found', message: raw || 'Не найдено', rawDetail: raw, status };
  }
  if (status === 413) {
    return { code: 'file_too_large', message: 'Файл превышает лимит 20 МБ', rawDetail: raw, status };
  }
  if (status === 503) {
    return {
      code: 'not_configured',
      message: 'Канал не настроен на сервере, обратитесь к администратору.',
      rawDetail: raw,
      status,
    };
  }

  for (const rule of ANCHOR_RULES) {
    if (rule.status === status && rule.matches.some((m) => lower.includes(m))) {
      return { code: rule.code, message: rule.message, rawDetail: raw, status };
    }
  }

  if (status === 422) {
    return { code: 'validation', message: raw || 'Неверные данные запроса', rawDetail: raw, status };
  }

  return { code: 'unknown', message: raw || 'Неизвестная ошибка', rawDetail: raw, status };
}

// ─────────────── Response helpers ───────────────

export async function parseTelegramErrorResponse(res: Response): Promise<TelegramParsedError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // body — не JSON
  }
  return classifyTelegramError(body, res.status);
}

export function networkError(message?: string): TelegramParsedError {
  return { code: 'network', message: message || 'Нет соединения с сервером', rawDetail: message || '' };
}

export class TelegramApiError extends Error {
  readonly code: TelegramErrorCode;
  readonly status?: number;
  readonly rawDetail: string;
  readonly retryAfterSeconds?: number;
  readonly validationFields?: TelegramParsedError['validationFields'];

  constructor(parsed: TelegramParsedError) {
    super(parsed.message);
    this.name = 'TelegramApiError';
    this.code = parsed.code;
    this.status = parsed.status;
    this.rawDetail = parsed.rawDetail;
    this.retryAfterSeconds = parsed.retryAfterSeconds;
    this.validationFields = parsed.validationFields;
  }
}

/** Человекочитаемое сообщение из любой пойманной ошибки. */
export function getTelegramErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}
